/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { parameterForceRequote, parameterAffectPanelPrice } from 'pu-data/Utils';
import { ERROR, QUOTED, PRICE_SELECTED, START } from 'pu-data/QuoteSteps';
import SimpleReactValidator from 'pu-utils/simple-react-validator';

import { getQuote, emailQuote } from 'api/apiQuote';
import { orderProduct } from 'api/order';

import { changeTab } from 'product_tabs/tabs/tabSlice';
import { TAB_FABRICATION } from 'product_tabs/tabs/data/tabType';
import { showErrors } from 'product_tabs/alerts/alertSlice';
import { validator as validatorProject } from 'product_tabs/tab_project/utils/validator';

import defaultState from './data/defaultState';
import initialState from './data/initialState';

import { changeParameterByName } from './slice/onChangeParameter';
import { updatePanelSize } from './slice/panelize';
import { buildData } from './utils/builders';

import { changeGlobalParameter } from 'product_tabs/actions';
import { isParameterNameQtyValid, isProductTypeParameterNameQtyValid } from 'pu-utils/validate';

export const doQuote = createAsyncThunk(
  'fabrication/doQuote',
  async (args, thunkAPI) => {
    const state = thunkAPI.getState().fabrication;
    const { parameters, suppliers } = state;
    const { row_selected, column_selected, supplier_selected, vendor_selected, ...rest } = parameters;

    const token = document.getElementsByName('authenticity_token')[0].value;
    const variant = document.getElementsByName('variant_id')[0].value;
    const data = {};
    suppliers.forEach((s) => {
      data[s] = rest;
    });

    const promise = getQuote({
      data, token, variant, quoteNumber: parameters.product_quote_number,
    });

    return promise
      .then((res) => res.data)
      .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
  },
);

export const doEmailQuote = createAsyncThunk('fabrication/doEmailQuote', async (args, thunkAPI) => {
  const { data, token } = args;
  const promise = emailQuote({ data, token });
  return promise
    .then((res) => res.data)
    .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
});

export const validator = new SimpleReactValidator({});

const fabricationService = createSlice({
  name: 'fabricationService',
  initialState: {
    ...initialState,
    validatorFlag: false,
  },
  reducers: {
    changeParameter(state, action) {
      const { name, value } = action.payload
      changeParameterByName(state.parameters, name, value);
      if (parameterAffectPanelPrice(name)) {
        updatePanelSize(state.parameters);
      }
      if(isParameterNameQtyValid(name) && !state.quantitiesChanged) {
        state.quantitiesChanged = true
      }

      state.parametersChanged = parameterForceRequote(name);
    },
    priceSelected(state, action) {
      const sp = action.payload;
      state.parameters.supplier_selected = sp.supplier;
      state.parameters.vendor_selected = sp.supplier;
      state.parameters.combined_selected = sp.combined ? '1' : '0';
      state.parameters.row_selected = sp.row_selected;
      state.parameters.column_selected = sp.column_selected;

      state.quoteStep = PRICE_SELECTED;
    },
    deleteFabrication(state) {
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();
      const newState = _.cloneDeep({ ...defaultState, validatorFlag: false });
      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.destroy = true;
      return newState;
    },
    updateValidatorFlag(state) {
      state.validatorFlag = !state.validatorFlag;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload
        if (isProductTypeParameterNameQtyValid(name, 'assembly') && !state.quantitiesChanged && state.quoteStep == START) {
          const qty_index = Number(name.split('_qty')[1]) - 1
          state.parameters.quantities[qty_index] = value
        }
      })
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.supplier_selected = '';
        state.parameters.vendor_selected = '';
        state.parameters.combined_selected = '';

        if (!quote || !quote.line_items) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quote = {};
        state.quoteStep = ERROR;
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state, action) => {
        state.loading = false;
      })
  },
});

export const {
  changeParameter,
  priceSelected,
  updateValidatorFlag,
  deleteFabrication,
} = fabricationService.actions;

export const validate = () => (dispatch, getState) => {
  const { quoteStep, parametersChanged } = getState().fabrication;

  let validateResult = true;

  if (quoteStep === PRICE_SELECTED) {
    if (!validator.allValid()) {
      validator.showMessages();
      dispatch(updateValidatorFlag());
      dispatch(changeTab(TAB_FABRICATION));
      dispatch(showErrors('Please fix the errors in Fabrication form'));
      validateResult = false;
      setTimeout(() => {
        document.querySelector('.pcb-fabrication .srv-validation-message').scrollIntoView(false);
      }, 500);
    } else {
      if (parametersChanged) {
        dispatch(showErrors('One or more parameters have changed. Please get a new Fabrication quote'));
        dispatch(changeTab(TAB_FABRICATION));
        validateResult = false;
      }
    }
  }

  if (!validatorProject.allValid()) {
    validatorProject.showMessages();
    let errors = validatorProject.getErrorMessages();
    let foundErrors = [];
    if(errors['Part Number'] && errors['Part Number'] != "") foundErrors.push('Please enter the part number for your project.');
    if(errors['Revision'] && errors['Revision'] != "") foundErrors.push('Please enter your revision.');
    if(errors['Zip file'] && errors['Zip file'] != "") foundErrors.push('Please upload your Zip File');
    dispatch(showErrors(foundErrors));
    dispatch(updateValidatorFlag());
    validateResult = false;
    setTimeout(() => {
      document.querySelector('.project-information .srv-validation-message').scrollIntoView(false);
    }, 500);
  }
  return validateResult;
}

export const destroy = (list) => (dispatch, getState) => {
  const state = getState().fabrication;

  if (state.destroy && state.lineItemId) {
    return list.concat(state.lineItemId)
  }
  return list;
}

export const addToCart = (args) => async (dispatch, getState) => {
  const { projectNumber, projectReference } = args;
  const state = getState().fabrication;


  const data = buildData({
    projectNumber,
    projectReference,
    parameters: state.parameters,
    quoteStep: state.quoteStep,
    variantId: state.currentVariant,
  });

  if (!data) return;

  try {
    await orderProduct(data);
  } catch (err) {
    console.error(err)
  }
}

export default fabricationService.reducer;
